'use client'

import { useMemo, useState } from 'react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  CalculatorIcon,
  CalendarIcon,
  DownloadIcon,
  EyeIcon,
  FolderIcon,
  HashIcon,
  ListIcon,
  MoreHorizontalIcon,
  PackageIcon,
  RefreshCcwIcon,
  SquareMenuIcon,
  SquarePlusIcon,
  Trash2Icon,
  WaypointsIcon,
  RefreshCwIcon
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { JobStatusEnum, SortEnum } from '@/enums'
import {
  useBulkDeleteProductExcelImports,
  useDeleteProductExcelImport,
  useDownloadProductExcelImportTemplate,
  useProductExcelImports,
  useRetryProductExcelImport
} from '@/hooks'
import { useProductExcelImportsStore } from '@/store'
import { ProductExcelImportsFilterParams, ProductExcelImportType } from '@/types/product-excel-import'
import {
  CategoryPath,
  ConfirmationDialog,
  DeleteConfirmationDialog,
  JobStatusBadge,
  LoadingState,
  ProductExcelImportsFilters,
  SortableHeader
} from '@/components/partials'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from '@/components/ui/context-menu'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { DataTablePagination } from '@/components/ui/data-table-pagination'
import { BulkDeleteResponse } from '@/types/general'
import { formatDateTime, formatNumber, tOptional } from '@/lib/utils'

const List = () => {
  const locale = useLocale()
  const t = useTranslations()
  const router = useRouter()
  const searchParams = useSearchParams()
  const queryClient = useQueryClient()
  const [deleteProductExcelImportId, setDeleteProductExcelImportId] = useState<string | null>(null)
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState<boolean>(false)
  const [retryingProductExcelImportId, setRetryingProductExcelImportId] = useState<string | null>(null)
  const [retryConfirmProductExcelImportId, setRetryConfirmProductExcelImportId] = useState<string | null>(null)

  const { mutate: deleteProductExcelImportMutate, isPending: isDeleting } = useDeleteProductExcelImport()
  const { mutate: bulkDeleteProductExcelImportsMutate, isPending: isBulkDeleting } = useBulkDeleteProductExcelImports()
  const { mutate: retryProductExcelImportMutate } = useRetryProductExcelImport()
  const { mutate: downloadTemplateMutate, isPending: isDownloadingTemplate } = useDownloadProductExcelImportTemplate()

  const selectedProductExcelImportIds = useProductExcelImportsStore(state => state.selectedProductExcelImportIds)
  const toggleProductExcelImportSelection = useProductExcelImportsStore(
    state => state.toggleProductExcelImportSelection
  )
  const selectAllProductExcelImports = useProductExcelImportsStore(state => state.selectAllProductExcelImports)
  const deselectAllProductExcelImports = useProductExcelImportsStore(state => state.deselectAllProductExcelImports)

  const filters = useMemo<ProductExcelImportsFilterParams>(() => {
    const params = new URLSearchParams(searchParams.toString())

    // Parse statuses from URL (comma-separated string to array)
    const statusesParam = params.get('statuses')
    const statuses = statusesParam ? statusesParam.split(',').filter(Boolean) : undefined

    // Parse categories from URL (comma-separated string to array)
    const categoriesParam = params.get('categories')
    const categories = categoriesParam ? categoriesParam.split(',').filter(Boolean) : undefined

    return {
      page: parseInt(params.get('page') || '1'),
      size: parseInt(params.get('size') || '20'),
      sort: (params.get('sort') as SortEnum) || SortEnum.DESC,
      sortBy: params.get('sortBy') || 'createdAt',
      q: params.get('q') || '',
      statuses,
      categories,
      createdAtStart: params.get('createdAtStart') || undefined,
      createdAtEnd: params.get('createdAtEnd') || undefined,
      updatedAtStart: params.get('updatedAtStart') || undefined,
      updatedAtEnd: params.get('updatedAtEnd') || undefined
    }
  }, [searchParams])
  const { data: productExcelImportsData, isLoading, error, refetch, isFetching } = useProductExcelImports(filters)

  // Update URL when filters change
  const updateUrlParams = (newFilters: Partial<ProductExcelImportsFilterParams>) => {
    const params = new URLSearchParams()
    const updatedFilters = { ...filters, ...newFilters }

    Object.entries(updatedFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        // Handle array values (like statuses)
        if (Array.isArray(value)) {
          if (value.length > 0) {
            params.set(key, value.join(','))
          }
        } else {
          params.set(key, value.toString())
        }
      }
    })

    router.push(`?${params.toString()}`)
  }

  const handleFiltersChange = (newFilters: Partial<ProductExcelImportsFilterParams>) => {
    updateUrlParams(newFilters)
  }

  const handleResetFilters = () => {
    router.push('/products/import/excel')
  }

  const handleSort = (sortBy: string, sort: SortEnum) => {
    handleFiltersChange({ sortBy, sort, page: 1 })
  }

  const handlePageChange = (page: number) => {
    handleFiltersChange({ page })
  }

  const handlePageSizeChange = (size: number) => {
    handleFiltersChange({ size, page: 1 })
  }

  const handleView = (productExcelImportId: string) => {
    router.push(`/products/import/excel/${productExcelImportId}`)
  }

  const handleRetry = (productExcelImportId: string) => {
    setRetryConfirmProductExcelImportId(productExcelImportId)
  }

  const confirmRetry = () => {
    if (retryConfirmProductExcelImportId) {
      setRetryingProductExcelImportId(retryConfirmProductExcelImportId)
      retryProductExcelImportMutate(retryConfirmProductExcelImportId, {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['productExcelImports'] })
          // Reset to page 1 when retrying
          handleFiltersChange({ page: 1 })
          toast.success(t('retry_started'))
          setRetryingProductExcelImportId(null)
          setRetryConfirmProductExcelImportId(null)
        },
        onError: (error: any) => {
          console.error('Error retrying productExcelImport:', error)
          toast.error(error.response?.data.message ?? t('error_retrying'))
          setRetryingProductExcelImportId(null)
          setRetryConfirmProductExcelImportId(null)
        }
      })
    }
  }

  const handleDelete = (productExcelImportId: string) => {
    setDeleteProductExcelImportId(productExcelImportId)
  }

  const renderActionItems = (productExcelImport: ProductExcelImportType, isDropdown = true) => {
    const ItemComponent = isDropdown ? DropdownMenuItem : ContextMenuItem

    return (
      <>
        <ItemComponent
          onClick={e => {
            e.stopPropagation()
            handleView(productExcelImport.id)
          }}>
          <EyeIcon className="h-4 w-4" />
          {t('excel_import_details')}
        </ItemComponent>
        {productExcelImport.status === JobStatusEnum.FAILED && (
          <ItemComponent
            onClick={e => {
              e.stopPropagation()
              handleRetry(productExcelImport.id)
            }}
            disabled={retryingProductExcelImportId === productExcelImport.id}>
            <RefreshCcwIcon className="h-4 w-4" />
            {t('retry')}
          </ItemComponent>
        )}
        <ItemComponent
          onClick={e => {
            e.stopPropagation()
            handleDelete(productExcelImport.id)
          }}
          className="text-destructive focus:text-destructive">
          <Trash2Icon className="h-4 w-4" />
          {t('delete')}
        </ItemComponent>
      </>
    )
  }

  const confirmDeleteMessage = () => {
    if (deleteProductExcelImportId) {
      deleteProductExcelImportMutate(deleteProductExcelImportId, {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['productExcelImports'] })
          toast.success(t('deleted_successfully'))
          setDeleteProductExcelImportId(null)
        },
        onError: (error: any) => {
          console.error('Error deleting model:', error)
          toast.error(error.response?.data.message ?? t('error_deleting'))
          setDeleteProductExcelImportId(null)
        }
      })
    }
  }

  const handleBulkDelete = () => {
    if (selectedProductExcelImportIds.length > 0) {
      setBulkDeleteConfirmOpen(true)
    }
  }

  const confirmBulkDeleteProductExcelImports = () => {
    if (selectedProductExcelImportIds.length > 0) {
      bulkDeleteProductExcelImportsMutate(selectedProductExcelImportIds, {
        onSuccess: async (response: BulkDeleteResponse) => {
          await queryClient.invalidateQueries({ queryKey: ['productExcelImports'] })
          if (response.deletedItems && response.deletedItems.length > 0) {
            toast.success(t('deleted_successfully'))
          }
          setBulkDeleteConfirmOpen(false)
        },
        onError: (error: any) => {
          if (error.response) {
            if (error.response.status === 422) {
              toast.error(error.response.data?.items?.ids || t('error_deleting'))
            } else {
              toast.error(t('error_deleting'))
            }
          } else {
            toast.error(t('error_deleting'))
          }

          setBulkDeleteConfirmOpen(false)
        }
      })
    }
  }

  const handleSelectAll = () => {
    if (productExcelImportsData && productExcelImportsData.items.length > 0) {
      if (selectedProductExcelImportIds.length === productExcelImportsData.items.length) {
        // If all productExcelImports are selected, deselect all
        deselectAllProductExcelImports()
      } else {
        // Otherwise, select all productExcelImports
        selectAllProductExcelImports(productExcelImportsData.items)
      }
    }
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="grid gap-3 sm:flex sm:items-center sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ListIcon className="h-5 w-5" />
                {t('excel_imports')}
              </CardTitle>
            </div>
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching || isLoading}>
                <RefreshCwIcon className={`h-4 w-4${isFetching ? 'animate-spin' : ''}`} />
              </Button>
              <Button variant="outline" onClick={() => {}} asChild>
                <Link href={`/products${searchParams.toString() ? `?${searchParams.toString()}` : ''}`}>
                  <PackageIcon />
                  <span className="hidden items-center gap-2 lg:flex">{t('products')}</span>
                </Link>
              </Button>
              <Button variant="outline" onClick={() => downloadTemplateMutate()} disabled={isDownloadingTemplate}>
                <DownloadIcon className="h-4 w-4" />
                <span className="hidden items-center gap-2 lg:flex">{t('excel_template_for_import')}</span>
              </Button>
              <Button asChild>
                <Link href="/products/import/excel/create">
                  <SquarePlusIcon />
                  <span className="hidden items-center gap-2 lg:flex">{t('create_excel_import')}</span>
                </Link>
              </Button>
              {selectedProductExcelImportIds.length > 0 && (
                <Button variant="destructive" onClick={handleBulkDelete}>
                  <Trash2Icon className="h-4 w-4" />
                  <div className="hidden items-center gap-2 lg:flex">
                    <span>{t('delete_selected')}</span>
                    <span>({selectedProductExcelImportIds.length})</span>
                  </div>
                </Button>
              )}
              <ProductExcelImportsFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onResetFilters={handleResetFilters}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <LoadingState isLoading={isLoading} error={error?.message} onRetry={refetch} loadingRows={5}>
            {productExcelImportsData && (
              <div className="space-y-4">
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12.5 text-center">
                          <div className="flex items-center justify-center">
                            <Checkbox
                              checked={
                                productExcelImportsData &&
                                productExcelImportsData.items.length > 0 &&
                                selectedProductExcelImportIds.length === productExcelImportsData.items.length
                              }
                              onCheckedChange={() => {
                                handleSelectAll()
                              }}
                              onClick={e => {
                                e.stopPropagation()
                              }}
                              className="cursor-pointer"
                            />
                          </div>
                        </TableHead>
                        <TableHead className="min-w-50">
                          <SortableHeader
                            sortBy="id"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <HashIcon className="h-4 w-4" />
                            ID
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50">
                          <SortableHeader
                            sortBy="category"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <div className="flex items-center gap-2">
                              <FolderIcon size={16} />
                              {t('category')}
                            </div>
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-right">
                          <SortableHeader
                            sortBy="createdTotal"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalculatorIcon className="h-4 w-4" />
                            {t('created_items')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-right">
                          <SortableHeader
                            sortBy="updatedTotal"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalculatorIcon className="h-4 w-4" />
                            {t('updated_items')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-right">
                          <SortableHeader
                            sortBy="failedTotal"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalculatorIcon className="h-4 w-4" />
                            {t('failed_items')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-right">
                          <SortableHeader
                            sortBy="total"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalculatorIcon className="h-4 w-4" />
                            {t('total')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-center">
                          <SortableHeader
                            sortBy="status"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <WaypointsIcon className="h-4 w-4" />
                            {t('status')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-center">
                          <SortableHeader
                            sortBy="createdAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="mr-2 w-4" />
                            {t('created_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-center">
                          <SortableHeader
                            sortBy="updatedAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="mr-2 w-4" />
                            {t('updated_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="w-25 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <SquareMenuIcon className="h-4 w-4" />
                            {t('actions')}
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {productExcelImportsData.items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={11} className="h-24 text-center">
                            {t('no_results')}
                          </TableCell>
                        </TableRow>
                      ) : (
                        productExcelImportsData.items.map((productExcelImport, index) => (
                          <ContextMenu key={productExcelImport.id}>
                            <ContextMenuTrigger asChild>
                              <TableRow
                                className={`hover:bg-muted/50 cursor-pointer transition-colors ${
                                  index % 2 === 0 ? 'bg-background' : 'bg-muted/25'
                                }`}
                                onClick={() => handleView(productExcelImport.id)}>
                                <TableCell className="text-center">
                                  <div className="flex items-center justify-center">
                                    <Checkbox
                                      checked={selectedProductExcelImportIds.includes(productExcelImport.id)}
                                      onCheckedChange={() => toggleProductExcelImportSelection(productExcelImport.id)}
                                      onClick={e => {
                                        e.stopPropagation()
                                      }}
                                      className="cursor-pointer"
                                    />
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="flex flex-col">
                                    <span className="text-muted-foreground text-xs">{productExcelImport.id}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="px-5">
                                  <CategoryPath category={productExcelImport.category} />
                                </TableCell>
                                <TableCell className="pr-5 text-right">
                                  {formatNumber(productExcelImport.createdTotal, locale)}
                                </TableCell>
                                <TableCell className="pr-5 text-right">
                                  {formatNumber(productExcelImport.updatedTotal, locale)}
                                </TableCell>
                                <TableCell className="pr-5 text-right">
                                  {formatNumber(productExcelImport.failedTotal, locale)}
                                </TableCell>
                                <TableCell className="pr-5 text-right">
                                  {formatNumber(productExcelImport.total, locale)}
                                </TableCell>
                                <TableCell className="text-center">
                                  <Tooltip>
                                    <TooltipTrigger>
                                      <div>
                                        <JobStatusBadge status={productExcelImport.status} />
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      {productExcelImport.message ? tOptional(t, productExcelImport.message) : ''}
                                    </TooltipContent>
                                  </Tooltip>
                                </TableCell>
                                <TableCell className="text-center">
                                  <div className="text-muted-foreground text-sm">
                                    <span>{formatDateTime(productExcelImport.createdAt, locale)}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <div className="text-muted-foreground text-sm">
                                    <span>{formatDateTime(productExcelImport.updatedAt, locale)}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        onClick={e => {
                                          e.preventDefault()
                                          e.stopPropagation()
                                        }}>
                                        <span className="sr-only">{t('open_menu')}</span>
                                        <MoreHorizontalIcon className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      {renderActionItems(productExcelImport, true)}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </TableCell>
                              </TableRow>
                            </ContextMenuTrigger>
                            <ContextMenuContent>{renderActionItems(productExcelImport, false)}</ContextMenuContent>
                          </ContextMenu>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                <DataTablePagination
                  currentPage={productExcelImportsData.page}
                  totalPages={productExcelImportsData.pages}
                  totalItems={productExcelImportsData.total}
                  pageSize={productExcelImportsData.size}
                  onPageChangeAction={handlePageChange}
                  onPageSizeChangeAction={handlePageSizeChange}
                />
              </div>
            )}
          </LoadingState>
        </CardContent>
      </Card>

      <DeleteConfirmationDialog
        open={deleteProductExcelImportId !== null}
        onCancel={() => setDeleteProductExcelImportId(null)}
        onConfirm={confirmDeleteMessage}
        isDeleting={isDeleting}
      />

      <DeleteConfirmationDialog
        open={bulkDeleteConfirmOpen}
        onCancel={() => setBulkDeleteConfirmOpen(false)}
        onConfirm={confirmBulkDeleteProductExcelImports}
        isDeleting={isBulkDeleting}
      />

      <ConfirmationDialog
        open={retryConfirmProductExcelImportId !== null}
        onCancel={() => setRetryConfirmProductExcelImportId(null)}
        onConfirm={confirmRetry}
        isLoading={retryingProductExcelImportId !== null}
        description={t('retry_confirmation_message')}
        confirmText={t('yes')}
        cancelText={t('no')}
        loadingText={t('retrying')}
      />
    </>
  )
}

export default List
